import { useGetRequestProducts, useGetRequestProductsStats, useUpdateRequestProductStatus } from '@practium/features/api';
import type { RequestProduct } from '@practium/features/products/types';
import { fuzzyFilter } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import { useToast } from '@practium/ui';
import { useEffect, useMemo, useState } from 'react';
import { buildUnionDateRange, isInDateRange } from '../../../shared/utils/dateFilter.utils';
import type { ProductRequestsTab } from '../types/productRequest.types';
import { CATEGORY_LABELS } from '../types/productRequest.types';

const PAGE_SIZE = 10;
const REQUESTS_REFETCH_INTERVAL = 15_000;

const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Categoria',
		options: [
			{ value: 'categoria:fertilizer', label: CATEGORY_LABELS.fertilizer },
			{ value: 'categoria:lime', label: CATEGORY_LABELS.lime },
			{ value: 'categoria:conditioner', label: CATEGORY_LABELS.conditioner },
			{ value: 'categoria:organic', label: CATEGORY_LABELS.organic },
			{ value: 'categoria:special', label: CATEGORY_LABELS.special },
			{ value: 'categoria:defensive', label: CATEGORY_LABELS.defensive },
		],
	},
	{
		label: 'Período',
		options: [
			{ value: 'periodo:15', label: 'Últimos 15 dias' },
			{ value: 'periodo:30', label: 'Últimos 30 dias' },
			{ value: 'periodo:custom', label: 'Personalizado' },
		],
	},
];

export const useProductRequestsPage = () => {
	const toast = useToast();
	const [activeTab, setActiveTab] = useState<ProductRequestsTab>('pending');
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [page, setPage] = useState(1);
	const [selectedRequest, setSelectedRequest] = useState<RequestProduct | null>(null);

	const { data: stats, isLoading: isLoadingStats } = useGetRequestProductsStats({
		refetchInterval: REQUESTS_REFETCH_INTERVAL,
	});
	const { data: requests, isLoading } = useGetRequestProducts({
		refetchInterval: REQUESTS_REFETCH_INTERVAL,
	});
	const { mutateAsync: updateStatus } = useUpdateRequestProductStatus();



	useEffect(() => {
		setPage(1);
	}, [searchQuery, filterValues, activeTab, customDateFrom, customDateTo]);

	const handleTabChange = (tab: ProductRequestsTab) => {
		setActiveTab(tab);
		setSearchQuery('');
		setFilterValues([]);
		setPage(1);
	};

	const processedRequests = useMemo(() => {
		let result = requests;

		if (activeTab !== 'all') {
			const statusMap: Record<Exclude<ProductRequestsTab, 'all'>, string> = {
				pending: 'PENDING',
				approved: 'APPROVED',
				rejected: 'REJECTED',
			};
			result = result.filter(r => r.status === statusMap[activeTab]);
		}

		result = fuzzyFilter(result, searchQuery, r =>
			[r.name, r.manufacturer, r.userName, r.category].join(' ')
		);

		const categoryFilters = filterValues.filter(v => v.startsWith('categoria:')).map(v => v.slice(10));
		if (categoryFilters.length > 0) {
			result = result.filter(r => categoryFilters.includes(r.category));
		}

		const periodoFilters = filterValues.filter(v => v.startsWith('periodo:')).map(v => v.slice(8));
		if (periodoFilters.length > 0) {
			const { from, to } = buildUnionDateRange(periodoFilters, customDateFrom, customDateTo);
			if (from || to) result = result.filter(r => isInDateRange(r.createdAt, from, to));
		}

		if (sortAsc) {
			result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		}

		return result;
	}, [requests, activeTab, searchQuery, filterValues, sortAsc, customDateFrom, customDateTo]);

	const totalPages = useMemo(
		() => Math.max(1, Math.ceil(processedRequests.length / PAGE_SIZE)),
		[processedRequests.length]
	);

	const filteredRequests = useMemo(
		() => processedRequests.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedRequests, page]
	);


	const handleUpdateStatus = async (status: 'APPROVED' | 'REJECTED') => {
		if (!selectedRequest) return;
		try {
			await updateStatus({ id: selectedRequest._id, status });
			setSelectedRequest(null);
			toast.success('Status atualizado com sucesso.');
		} catch {
			toast.error('Erro ao atualizar o status. Tente novamente.');
		}
	};


	return {
		filteredRequests,
		isLoading: isLoadingStats || isLoading,
		activeTab,
		setActiveTab: handleTabChange,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort: () => setSortAsc(v => !v),
		filterValues,
		setFilterValues,
		filterGroups: FILTER_GROUPS,
		customDateFrom,
		setCustomDateFrom,
		customDateTo,
		setCustomDateTo,
		page,
		setPage,
		totalPages,
		selectedRequest,
		setSelectedRequest,
		handleUpdateStatus,
		stats
	};
};
